import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Search,
  Star,
  Archive,
  AlertCircle,
  Send,
  Smile,
  ChevronDown,
  Filter,
  Settings,
  Loader,
  MessageSquare,
  User,
  Link2,
  Activity,
  TrendingUp,
  Zap,
  Instagram,
  Twitter,
  Facebook,
  Music,
  Linkedin,
  Youtube,
  MessageCircle,
  Paperclip,
  Phone,
  Copy,
  Eye,
  Bell,
  Check,
  Edit3,
  Clock3,
  AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

// ============================================================================
// TYPES
// ============================================================================

interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  sender_name: string;
  sender_avatar?: string;
  platform: 'instagram' | 'twitter' | 'facebook' | 'tiktok' | 'linkedin' | 'youtube' | 'email' | 'sms';
  content: string;
  timestamp: string;
  is_reply: boolean;
  is_internal_note: boolean;
  priority: 'urgent' | 'high' | 'normal' | 'low';
  sentiment: 'positive' | 'neutral' | 'negative';
  is_unread: boolean;
  assigned_to?: string;
  assigned_avatar?: string;
  attachments?: Attachment[];
  tags?: string[];
}

interface Conversation {
  id: string;
  latest_message_id: string;
  reply_target_message_id: string;
  contact_id?: string;
  sender_id: string;
  sender_name: string;
  sender_avatar?: string;
  platform: string;
  last_message: string;
  preview_text: string;
  timestamp: string;
  is_unread: boolean;
  is_starred: boolean;
  is_archived: boolean;
  priority: 'urgent' | 'high' | 'normal' | 'low';
  sentiment: 'positive' | 'neutral' | 'negative';
  assigned_to?: string;
  assigned_avatar?: string;
  unread_count: number;
  message_count: number;
  follower_count?: number;
  platform_icon?: string;
}

interface Contact {
  id: string;
  merged_identity: string;
  email?: string;
  phone?: string;
  platforms: {
    platform: string;
    handle: string;
    follower_count: number;
    verified: boolean;
  }[];
  tags: string[];
  segments: string[];
  lead_score: number;
  lifecycle_stage: 'lead' | 'prospect' | 'customer' | 'advocate';
  notes: string;
  previous_conversations: number;
  lifetime_value?: number;
  last_seen?: string;
}

interface CannedResponse {
  id: string;
  title: string;
  content?: string;
  text?: string;
  category?: string;
  created_at: string;
}

interface Attachment {
  id: string;
  url: string;
  filename: string;
  type: string;
  size: number;
}

interface InboxStats {
  unread_count: number;
  avg_response_time_minutes: number;
  sla_breaches_today: number;
  total_conversations: number;
  conversations_this_week: number;
}

interface BulkActionPayload {
  message_ids: string[];
  action: 'archive' | 'assign' | 'tag' | 'mark_read' | 'mark_unread';
  assigned_to?: string;
  tags?: string[];
}

// ============================================================================
// API INTEGRATION
// ============================================================================

const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const inboxAPI = {
  getMessages: (params: Record<string, unknown>) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (Array.isArray(value)) {
        value.forEach((item) => searchParams.append(key, String(item)));
        return;
      }
      searchParams.append(key, String(value));
    });
    return api.get('/inbox/messages', { params: searchParams });
  },
  getMessage: (id: string) => api.get(`/inbox/messages/${id}`),
  replyToMessage: (id: string, data: unknown) => api.post(`/inbox/messages/${id}/reply`, data),
  updateMessage: (id: string, data: unknown) => api.put(`/inbox/messages/${id}`, data),
  bulkUpdate: (data: BulkActionPayload) => api.post('/inbox/messages/bulk', data),
  getConversation: (id: string) => api.get(`/inbox/conversations/${id}`),
  getContact: (id: string) => api.get(`/inbox/contacts/${id}`),
  updateContact: (id: string, data: unknown) => api.put(`/inbox/contacts/${id}`, data),
  getCannedResponses: () => api.get('/inbox/canned-responses'),
  getStats: () => api.get('/inbox/stats'),
};

// ============================================================================
// CONSTANTS
// ============================================================================

const PLATFORMS = [
  { id: 'instagram', label: 'Instagram', icon: Instagram, color: 'text-pink-600', bg: 'bg-pink-50' },
  { id: 'twitter', label: 'Twitter', icon: Twitter, color: 'text-sky-600', bg: 'bg-sky-50' },
  { id: 'facebook', label: 'Facebook', icon: Facebook, color: 'text-blue-600', bg: 'bg-n-blue-bg' },
  { id: 'tiktok', label: 'TikTok', icon: Music, color: 'text-black', bg: 'bg-gray-50' },
  { id: 'linkedin', label: 'LinkedIn', icon: Linkedin, color: 'text-blue-700', bg: 'bg-blue-100' },
  { id: 'youtube', label: 'YouTube', icon: Youtube, color: 'text-red-600', bg: 'bg-red-50' },
  { id: 'email', label: 'Email', icon: MessageSquare, color: 'text-n-text-md', bg: 'bg-gray-50' },
  { id: 'sms', label: 'SMS', icon: MessageCircle, color: 'text-green-600', bg: 'bg-green-50' },
];

const PRIORITIES = ['urgent', 'high', 'normal', 'low'];

const PRIORITY_CONFIG: Record<string, { label: string; color: string; bg: string; dot: string }> = {
  urgent: { label: 'Urgent', color: 'text-red-600', bg: 'bg-red-50', dot: 'bg-red-500' },
  high: { label: 'High', color: 'text-orange-600', bg: 'bg-orange-50', dot: 'bg-orange-500' },
  normal: { label: 'Normal', color: 'text-blue-600', bg: 'bg-n-blue-bg', dot: 'bg-n-blue-bg0' },
  low: { label: 'Low', color: 'text-n-text-md', bg: 'bg-gray-100', dot: 'bg-gray-400' },
};

const SENTIMENT_CONFIG: Record<string, { color: string; label: string }> = {
  positive: { color: 'bg-green-500', label: 'Positive' },
  neutral: { color: 'bg-gray-400', label: 'Neutral' },
  negative: { color: 'bg-red-500', label: 'Negative' },
};

// ============================================================================
// COMPONENTS
// ============================================================================

function PlatformIcon({ platform, className = "w-4 h-4" }: { platform: string; className?: string }) {
  const p = PLATFORMS.find((x) => x.id === platform);
  if (!p) return null;
  const Icon = p.icon;
  return <Icon className={`${className} ${p.color}`} />;
}

function StatBadge({
  label,
  value,
  icon: Icon,
  color = 'blue',
  onClick
}: {
  label: string;
  value: number | string;
  icon: React.ElementType;
  color?: 'blue' | 'red' | 'green' | 'gray';
  onClick?: () => void;
}) {
  const colorConfig = {
    blue: { bg: 'bg-n-blue-bg', border: 'border-n-border', text: 'text-blue-700', iconBg: 'bg-blue-100/50', icon: 'text-blue-600' },
    red: { bg: 'bg-rose-50', border: 'border-rose-100', text: 'text-rose-700', iconBg: 'bg-rose-100/50', icon: 'text-rose-600' },
    green: { bg: 'bg-n-green-bg', border: 'border-n-border', text: 'text-n-green', iconBg: 'bg-emerald-100/50', icon: 'text-n-green' },
    gray: { bg: 'bg-n-page', border: 'border-n-border', text: 'text-n-text', iconBg: 'bg-slate-100/50', icon: 'text-n-text-md' },
  };

  const cfg = colorConfig[color];

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-2.5 rounded-n border transition-all duration-300 ${cfg.bg} ${cfg.border} ${cfg.text} hover:shadow-md hover:shadow-slate-200/50 active:scale-95 group shadow-sm shadow-slate-200/20`}
    >
      <div className={`p-2 rounded-xl ${cfg.iconBg} border border-white shadow-sm transition-transform group-hover:scale-110`}>
        <Icon className={`w-4 h-4 ${cfg.icon}`} />
      </div>
      <div className="flex flex-col items-start">
        <span className="text-[10px] font-bold uppercase tracking-widest opacity-80 leading-none mb-1">{label}</span>
        <span className="text-base font-black leading-none tracking-tight">{value}</span>
      </div>
    </button>
  );
}

function ConversationCard({
  conversation,
  isSelected,
  onSelect,
  onClickCard,
  isActive,
}: {
  conversation: Conversation;
  isSelected: boolean;
  onSelect: (e: React.MouseEvent) => void;
  onClickCard: () => void;
  isActive: boolean;
}) {
  const platformConfig = PLATFORMS.find((p) => p.id === conversation.platform);
  const priorityConfig = PRIORITY_CONFIG[conversation.priority] || PRIORITY_CONFIG.normal;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      whileHover={{ y: -2 }}
      className={`relative group mx-2 my-2 cursor-pointer transition-all duration-300 rounded-n border ${
        isActive
          ? 'bg-n-card border-emerald-500/30 shadow-md shadow-emerald-100/50 ring-1 ring-emerald-500/20 card-glow'
          : 'bg-n-card border-n-border/60 shadow-sm shadow-slate-200/40 hover:border-slate-300 hover:shadow-md hover:shadow-slate-200/60'
      }`}
      onClick={onClickCard}
    >
      <div className="p-4 flex gap-4">
        {/* Selection Checkbox - Sophisticated */}
        <div
          className="flex-shrink-0 pt-1.5"
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            onSelect(e);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onSelect(e as unknown as React.MouseEvent);
            }
          }}
        >
          <div className={`w-5 h-5 rounded-lg border flex items-center justify-center transition-all duration-200 ${
            isSelected 
              ? 'bg-blue-600 border-blue-600 shadow-sm' 
              : 'bg-n-page border-n-border hover:border-blue-400 group-hover:bg-n-card'
          }`}>
            {isSelected && <Check className="w-3.5 h-3.5 text-white stroke-[3px]" />}
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex items-center gap-3 min-w-0">
              <div className="relative flex-shrink-0">
                {conversation.sender_avatar ? (
                  <img
                    src={conversation.sender_avatar}
                    alt={conversation.sender_name}
                    className="w-10 h-10 rounded-xl object-cover border border-n-border shadow-sm"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-xl bg-slate-100 border border-n-border flex items-center justify-center text-slate-400 font-bold">
                    {conversation.sender_name.charAt(0)}
                  </div>
                )}
                <div className="absolute -bottom-1 -right-1 bg-n-card p-1 rounded-lg shadow-sm border border-slate-50">
                  <PlatformIcon platform={conversation.platform} className="w-3 h-3" />
                </div>
              </div>
              <div className="min-w-0">
                <p className="font-bold text-n-text text-[13px] tracking-tight truncate leading-tight">{conversation.sender_name}</p>
                <p className="text-[11px] font-bold text-n-text-lt uppercase tracking-widest mt-0.5">
                  {platformConfig?.label}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 pt-0.5">
              {conversation.is_starred && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500 drop-shadow-sm" />}
              <span className="text-[11px] font-bold text-n-text-lt">
                {new Date(conversation.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          </div>

          <p className={`text-sm line-clamp-2 mb-3 leading-relaxed ${conversation.is_unread ? 'font-semibold text-slate-800' : 'text-slate-500 font-medium'}`}>
            {conversation.preview_text}
          </p>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Status Indicators */}
            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-tight border ${
              priorityConfig.bg} ${priorityConfig.color} ${priorityConfig.bg.replace('bg-', 'border-').replace('50', '200')}`}>
              <div className={`w-1.5 h-1.5 rounded-full ${priorityConfig.dot} animate-pulse`} />
              {priorityConfig.label}
            </div>

            <div 
              className={`w-3 h-3 rounded-full border-2 border-white shadow-sm transition-transform hover:scale-125 ${SENTIMENT_CONFIG[conversation.sentiment].color}`} 
              title={`Sentiment: ${SENTIMENT_CONFIG[conversation.sentiment].label}`} 
            />

            {conversation.assigned_to && (
              <div className="flex items-baseline gap-1.5 ml-1">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">to</span>
                <img src={conversation.assigned_avatar} alt={conversation.assigned_to} className="w-6 h-6 rounded-lg ring-2 ring-white border border-n-border shadow-sm" />
              </div>
            )}

            {conversation.unread_count > 0 && (
              <span className="ml-auto bg-blue-600 text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center shadow-sm shadow-blue-200">
                {conversation.unread_count}
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function MessageBubble({ message, isOwn }: { message: Message; isOwn: boolean }) {
  const isNote = message.is_internal_note;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex mb-6 ${isOwn ? 'justify-end' : 'justify-start'}`}
    >
      <div
        className={`max-w-[85%] lg:max-w-md px-5 py-3.5 rounded-n shadow-sm border transition-all ${
          isNote
            ? 'bg-amber-50/50 border-amber-200 text-slate-800'
            : isOwn
              ? 'bg-slate-900 border-slate-800 text-white rounded-br-none shadow-slate-200/50'
              : 'bg-n-card border-n-border/60 text-slate-800 rounded-bl-none shadow-slate-200/20 shadow-sm'
        }`}
      >
        {isNote && (
          <div className="flex items-center gap-2 mb-2 pb-2 border-b border-amber-200/50">
            <Edit3 className="w-3 h-3 text-amber-600" />
            <span className="text-[10px] font-black uppercase tracking-widest text-amber-600 leading-none">Internal Note</span>
          </div>
        )}
        <p className="text-[13px] font-medium leading-relaxed break-words">{message.content}</p>
        <div className={`mt-2.5 flex items-center gap-2 ${isNote ? 'text-amber-500' : isOwn ? 'text-slate-400' : 'text-slate-400'}`}>
          <span className="text-[10px] font-bold">
            {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
          {isOwn && <Check className="w-3 h-3" />}
        </div>
      </div>
    </motion.div>
  );
}

function ReplyComposer({
  conversationId: _conversationId,
  onSend,
  isSending,
  cannedResponses,
}: {
  conversationId: string;
  onSend: (text: string, isNote: boolean) => Promise<void>;
  isSending: boolean;
  cannedResponses: CannedResponse[];
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [isNote, setIsNote] = useState(false);
  const [showCanned, setShowCanned] = useState(false);
  const [cannedSearch, setCannedSearch] = useState('');

  const filteredCanned = useMemo(() => {
    if (!cannedSearch) return cannedResponses;
    const query = cannedSearch.toLowerCase();
    return cannedResponses.filter((r) => {
      const content = r.content || r.text || '';
      return r.title.toLowerCase().includes(query) || content.toLowerCase().includes(query);
    });
  }, [cannedResponses, cannedSearch]);

  const handleSend = async () => {
    if (!text.trim()) return;
    try {
      await onSend(text, isNote);
      setText('');
    } catch (error) {
      console.error(error);
    }
  };

  const insertCanned = (content: string) => {
    setText((prev) => prev + '\n' + content);
    setShowCanned(false);
    setCannedSearch('');
  };

  return (
    <div className="bg-n-card rounded-n border border-n-border/60 shadow-lg shadow-slate-200/20 overflow-hidden">
      {/* Mode toggle - Premium */}
      <div className="flex bg-n-page border-b border-n-border p-1">
        <button
          onClick={() => setIsNote(false)}
          className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-xl text-xs font-bold uppercase tracking-widest transition-all duration-200 ${
            !isNote
              ? 'bg-n-card text-slate-900 shadow-sm'
              : 'text-slate-400 hover:text-n-text-md'
          }`}
        >
          <MessageCircle className="w-3.5 h-3.5" />
          {t('inbox_public_reply', 'Public Reply')}
        </button>
        <button
          onClick={() => setIsNote(true)}
          className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-xl text-xs font-bold uppercase tracking-widest transition-all duration-200 ${
            isNote
              ? 'bg-amber-100 text-amber-700 shadow-sm border border-amber-200/50'
              : 'text-slate-400 hover:text-n-text-md'
          }`}
        >
          <Edit3 className="w-3.5 h-3.5" />
          {t('inbox_internal_note', 'Internal Note')}
        </button>
      </div>

      <div className="p-4">
        {/* Text input - Premium */}
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={isSending}
          placeholder={isNote ? t('inbox_add_note', 'Add an internal note only visible to team...') : t('inbox_type_reply', 'Type your reply...')}
          className={`w-full px-4 py-3 border border-transparent rounded-xl focus:bg-n-card focus:border-blue-300 focus:ring-2 focus:ring-blue-500/20 resize-none text-[13px] font-medium transition-all ${
            isNote ? 'bg-amber-50/30 placeholder:text-amber-300' : 'bg-n-page placeholder:text-slate-400'
          }`}
          rows={3}
        />

        {/* Actions */}
        <div className="flex items-center gap-3 mt-4">
          <div className="flex items-center gap-1">
             {/* Emoji & Canned in a group */}
             <button className="p-2.5 hover:bg-slate-100 rounded-xl transition-all text-slate-400 hover:text-n-text-md" title="Add emoji">
               <Smile className="w-5 h-5" />
             </button>

            <div className="relative">
              <button
                onClick={() => setShowCanned(!showCanned)}
                className="p-2.5 hover:bg-slate-100 rounded-xl transition-all text-slate-400 hover:text-n-text-md flex items-center gap-1"
                title="Canned responses"
              >
                <Copy className="w-5 h-5" />
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showCanned ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {showCanned && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute bottom-full mb-3 left-0 w-80 bg-n-card border border-n-border/60 rounded-n shadow-2xl z-50 overflow-hidden backdrop-blur-xl"
                  >
                    <div className="p-3 bg-n-page border-b border-n-border">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                        <input
                          type="text"
                          placeholder={t('inbox_search_canned', 'Search responses...')}
                          value={cannedSearch}
                          onChange={(e) => setCannedSearch(e.target.value)}
                          className="w-full pl-9 pr-3 py-1.5 bg-n-card border border-gray-300 rounded-lg text-xs focus:outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-500/20"
                        />
                      </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto p-1 custom-scrollbar">
                      {filteredCanned.length === 0 ? (
                        <div className="p-8 text-center">
                          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">{t('inbox_no_canned', 'None found')}</p>
                        </div>
                      ) : (
                        filteredCanned.map((resp) => {
                          const content = resp.content || resp.text || '';
                          return (
                            <button
                              key={resp.id}
                              onClick={() => insertCanned(content)}
                              className="w-full text-left p-3 hover:bg-n-page rounded-xl transition-all group"
                            >
                              <p className="text-xs font-black text-slate-900 mb-0.5 group-hover:text-n-green transition-colors uppercase tracking-tight">{resp.title}</p>
                              <p className="text-[11px] text-slate-500 font-medium line-clamp-1 leading-normal">{content}</p>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <button className="p-2.5 hover:bg-slate-100 rounded-xl transition-all text-slate-400 hover:text-n-text-md" title="Add attachment">
              <Paperclip className="w-5 h-5" />
            </button>
          </div>

          <div className="ml-auto flex items-center gap-3">
            {/* AI suggestion button - Premium */}
            <button className="px-4 py-2.5 rounded-xl bg-purple-600 text-white text-[11px] font-black uppercase tracking-widest hover:bg-purple-700 transition-all flex items-center gap-2 shadow-lg shadow-purple-100 active:scale-95">
              <Zap className="w-3.5 h-3.5 fill-current" />
              {t('inbox_ai_reply', 'AI Suggest')}
            </button>

            {/* Send button - Premium */}
            <button
              onClick={handleSend}
              disabled={isSending || !text.trim()}
              className="px-6 py-2.5 rounded-xl bg-slate-900 text-white text-[11px] font-black uppercase tracking-widest hover:bg-black disabled:opacity-30 disabled:cursor-not-allowed transition-all flex items-center gap-2 shadow-xl shadow-slate-200 active:scale-95"
            >
              {isSending ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {t('inbox_send', 'Send')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ContactSidebar({
  contact,
  isLoading,
  onSaveNotes,
  isSavingNotes,
}: {
  contact: Contact | null;
  isLoading: boolean;
  onSaveNotes: (contactId: string, notes: string) => Promise<void>;
  isSavingNotes: boolean;
}) {
  const { t } = useTranslation();
  const [notesEdit, setNotesEdit] = useState(false);
  const [notes, setNotes] = useState(contact?.notes || '');

  useEffect(() => {
    setNotes(contact?.notes || '');
    setNotesEdit(false);
  }, [contact?.id, contact?.notes]);

  if (isLoading) {
    return (
      <div className="h-full bg-n-card p-8 flex flex-col items-center justify-center text-center">
        <div className="w-12 h-12 rounded-n bg-n-page border border-n-border flex items-center justify-center mb-4">
          <Loader className="w-6 h-6 animate-spin text-n-green/20" />
        </div>
        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Loading contact...</p>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="h-full bg-n-card p-8 flex flex-col items-center justify-center text-center">
        <div className="w-16 h-16 rounded-[2rem] bg-n-page border border-n-border flex items-center justify-center mb-6 shadow-inner">
          <User className="w-8 h-8 text-slate-300" />
        </div>
        <h3 className="text-sm font-black text-slate-900 tracking-tight mb-2 uppercase tracking-widest">{t('inbox_no_details', 'Profile')}</h3>
        <p className="text-xs text-slate-500 font-medium leading-relaxed max-w-[180px]">
          {t('inbox_select_conversation', 'Select a conversation to view details')}
        </p>
      </div>
    );
  }

  return (
    <div className="h-full bg-n-card flex flex-col overflow-y-auto custom-scrollbar">
      {/* Profile header - Premium */}
      <div className="p-6 border-b border-n-border/60">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="relative mb-4">
            <div className="w-20 h-20 rounded-[2.5rem] bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center text-white font-black text-2xl shadow-xl shadow-slate-200 ring-4 ring-white border border-n-border">
              {contact.merged_identity.charAt(0).toUpperCase()}
            </div>
            <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-xl border-2 border-white flex items-center justify-center shadow-lg bg-n-green-bg0 text-white`}>
              <Check className="w-3 h-3 stroke-[3px]" />
            </div>
          </div>
          <h3 className="text-lg font-black text-slate-900 tracking-tight leading-tight mb-1">{contact.merged_identity}</h3>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1">
            <Activity className="w-3 h-3" />
            Active {contact.last_seen ? new Date(contact.last_seen).toLocaleDateString() : 'recently'}
          </p>
        </div>

        {/* Quick info - Premium Cards */}
        <div className="grid grid-cols-2 gap-3 mb-2">
          <div className="bg-n-page/50 rounded-n p-4 border border-n-border transition-all hover:bg-n-page">
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{t('inbox_lead_score', 'Score')}</p>
            <p className="text-2xl font-black text-slate-900 flex items-center gap-1.5 leading-none">
              <TrendingUp className="w-5 h-5 text-n-green" />
              {contact.lead_score}
            </p>
          </div>
          <div className="bg-n-page/50 rounded-n p-4 border border-n-border transition-all hover:bg-n-page">
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{t('inbox_conversations', 'Chats')}</p>
            <p className="text-2xl font-black text-slate-900 leading-none">{contact.previous_conversations}</p>
          </div>
        </div>
      </div>

      <div className="flex-1">
        {/* Platform identities - Premium Rows */}
        <div className="p-6 border-b border-n-border/60 bg-n-page/30">
          <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
            <Link2 className="w-4 h-4" />
            {t('inbox_platform_ids', 'Social Identities')}
          </h4>
          <div className="space-y-2">
            {contact.platforms.map((p, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 rounded-n bg-n-card border border-n-border shadow-sm transition-all hover:border-emerald-500/30">
                <div className="flex items-center gap-3">
                  <div className="bg-n-page p-2 rounded-xl border border-n-border">
                    <PlatformIcon platform={p.platform} className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-[13px] font-black text-slate-900 tracking-tight leading-none mb-1">{p.handle}</p>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none">
                      {p.follower_count.toLocaleString()} FLWRs
                    </p>
                  </div>
                </div>
                {p.verified && <Check className="w-3.5 h-3.5 text-n-green stroke-[3px]" />}
              </div>
            ))}
          </div>
        </div>

        {/* Info Grid */}
        <div className="p-6 space-y-6">
          {/* Lifecycle and segments */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 whitespace-nowrap">{t('inbox_lifecycle', 'Lifecycle')}</p>
              <span className="inline-flex items-center px-3 py-1 rounded-xl bg-n-blue-bg text-blue-700 text-[10px] font-black uppercase tracking-widest border border-n-border">
                {contact.lifecycle_stage}
              </span>
            </div>
            {contact.lifetime_value && (
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 whitespace-nowrap">{t('inbox_lifetime_value', 'Total Value')}</p>
                <p className="font-black text-sm text-slate-900 tracking-tight">${contact.lifetime_value}</p>
              </div>
            )}
          </div>

          {contact.segments.length > 0 && (
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{t('inbox_segments', 'Segment Labels')}</p>
              <div className="flex flex-wrap gap-1.5">
                {contact.segments.map((seg) => (
                  <span key={seg} className="px-2.5 py-1 rounded-lg bg-n-green-bg text-n-green text-[10px] font-black uppercase tracking-widest border border-n-border/50">
                    {seg}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Contact Details */}
          <div>
             <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">{t('inbox_contact_info', 'Contact details')}</p>
            <div className="space-y-3">
              {contact.email && (
                <div className="flex items-center gap-3 p-3 bg-n-page/50 rounded-n border border-n-border">
                  <div className="p-1.5 bg-n-card rounded-lg shadow-sm">
                    <MessageSquare className="w-3.5 h-3.5 text-slate-400" />
                  </div>
                  <a href={`mailto:${contact.email}`} className="text-[11px] font-bold text-n-text hover:text-n-green truncate underline decoration-slate-200">
                    {contact.email}
                  </a>
                </div>
              )}
              {contact.phone && (
                <div className="flex items-center gap-3 p-3 bg-n-page/50 rounded-n border border-n-border">
                  <div className="p-1.5 bg-n-card rounded-lg shadow-sm">
                    <Phone className="w-3.5 h-3.5 text-slate-400" />
                  </div>
                  <a href={`tel:${contact.phone}`} className="text-[11px] font-bold text-n-text hover:text-n-green underline decoration-slate-200">
                    {contact.phone}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Notes Section */}
          <div className="bg-slate-900 rounded-3xl p-6 text-white shadow-xl shadow-slate-200 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-32 h-32 bg-n-green-bg0/20 blur-3xl rounded-full -translate-y-1/2 translate-x-1/2 group-hover:bg-n-green-bg0/30 transition-all duration-700" />
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-[10px] font-black uppercase tracking-widest text-emerald-400">{t('inbox_notes', 'Internal Intelligence')}</h4>
                <button
                  onClick={() => setNotesEdit(!notesEdit)}
                  className="p-1.5 hover:bg-n-card/10 rounded-xl transition-all"
                >
                  <Edit3 className="w-4 h-4 text-white/60 group-hover:text-white" />
                </button>
              </div>
              {notesEdit ? (
                <div className="space-y-3">
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full bg-n-card/10 border border-white/20 rounded-n p-3 text-xs placeholder:text-white/30 focus:outline-none focus:border-blue-300/50 focus:ring-2 focus:ring-blue-500/20 transition-all text-white"
                    rows={3}
                  />
                  <button
                    onClick={async () => {
                      await onSaveNotes(contact.id, notes);
                      setNotesEdit(false);
                    }}
                    disabled={isSavingNotes}
                    className="w-full py-2 bg-n-green-bg0 text-slate-900 text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-emerald-400 transition-all"
                  >
                    {isSavingNotes ? 'Saving...' : 'Save Insights'}
                  </button>
                </div>
              ) : (
                <p className="text-xs font-medium text-white/80 leading-relaxed italic">
                  &quot;{notes || t('inbox_no_notes', 'No intelligence recorded yet for this stakeholder.')}&quot;
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export default function UnifiedInboxPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // ---- State ----
  const [activeTab, setActiveTab] = useState<'all' | 'unread' | 'starred' | 'assigned'>('all');
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [selectedMessageIds, setSelectedMessageIds] = useState<Set<string>>(new Set());
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Debounce search input by 400ms
  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<string>>(new Set());
  const [selectedPriorities, setSelectedPriorities] = useState<Set<string>>(new Set());
  const [showPlatformFilter, setShowPlatformFilter] = useState(false);
  const [showPriorityFilter, setShowPriorityFilter] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // ---- Queries ----
  const { data: statsData } = useQuery({
    queryKey: ['inbox-stats'],
    queryFn: () => inboxAPI.getStats().then((r) => r.data),
    refetchInterval: 2 * 60_000,
    refetchIntervalInBackground: false,
    staleTime: 60_000,
  });

  const stats: InboxStats = statsData || {
    unread_count: 0,
    avg_response_time_minutes: 0,
    sla_breaches_today: 0,
    total_conversations: 0,
    conversations_this_week: 0,
  };

  const { data: messagesData, isLoading: isLoadingMessages } = useQuery({
    queryKey: ['inbox-messages', activeTab, searchQuery, Array.from(selectedPlatforms), Array.from(selectedPriorities)],
    queryFn: () =>
      inboxAPI.getMessages({
        filter: activeTab === 'all' ? undefined : activeTab,
        search: searchQuery || undefined,
        platforms: selectedPlatforms.size > 0 ? Array.from(selectedPlatforms) : undefined,
        priorities: selectedPriorities.size > 0 ? Array.from(selectedPriorities) : undefined,
      }).then((r) => r.data),
    staleTime: 15_000,
  });

  const conversations: Conversation[] = messagesData?.conversations || [];

  const { data: selectedConversationData, isLoading: isLoadingThread } = useQuery({
    queryKey: ['inbox-conversation', selectedConversationId],
    queryFn: () => (selectedConversationId ? inboxAPI.getConversation(selectedConversationId).then((r) => r.data) : null),
    enabled: !!selectedConversationId,
    staleTime: 10_000,
  });

  const { data: contactData, isLoading: isLoadingContact } = useQuery({
    queryKey: ['inbox-contact', selectedConversationData?.contact_id],
    queryFn: () =>
      selectedConversationData?.contact_id ? inboxAPI.getContact(selectedConversationData.contact_id).then((r) => r.data) : null,
    enabled: !!selectedConversationData?.contact_id,
    staleTime: 60_000,
  });

  const { data: cannedResponsesData } = useQuery({
    queryKey: ['inbox-canned-responses'],
    queryFn: () => inboxAPI.getCannedResponses().then((r) => r.data),
    staleTime: 60_000,
  });

  // ---- Mutations ----
  const replyMutation = useMutation({
    mutationFn: (payload: { text: string; isNote: boolean }) => {
      const targetMessageId = selectedConversationData?.reply_target_message_id;
      if (!targetMessageId) {
        return Promise.reject(new Error('No reply target is available for this conversation.'));
      }
      return inboxAPI.replyToMessage(targetMessageId, { content: payload.text, is_internal_note: payload.isNote });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inbox-conversation', selectedConversationId] });
      queryClient.invalidateQueries({ queryKey: ['inbox-messages'] });
      queryClient.invalidateQueries({ queryKey: ['inbox-stats'] });
      toast.success('Message sent!');
    },
    onError: () => toast.error('Failed to send message'),
  });

  const bulkActionMutation = useMutation({
    mutationFn: (payload: BulkActionPayload) => inboxAPI.bulkUpdate(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inbox-messages'] });
      queryClient.invalidateQueries({ queryKey: ['inbox-stats'] });
      setSelectedMessageIds(new Set());
      toast.success('Action completed');
    },
    onError: () => toast.error('Action failed'),
  });

  const updateMessageMutation = useMutation({
    mutationFn: (payload: { id: string; data: unknown }) => inboxAPI.updateMessage(payload.id, payload.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inbox-messages'] });
      queryClient.invalidateQueries({ queryKey: ['inbox-conversation', selectedConversationId] });
      queryClient.invalidateQueries({ queryKey: ['inbox-stats'] });
    },
    onError: () => toast.error('Failed to update'),
  });

  const updateContactMutation = useMutation({
    mutationFn: (payload: { id: string; data: unknown }) => inboxAPI.updateContact(payload.id, payload.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inbox-contact', selectedConversationData?.contact_id] });
      toast.success('Contact updated');
    },
    onError: () => toast.error('Failed to update contact'),
  });

  // ---- Handlers ----
  const handleSelectConversation = (id: string) => {
    setSelectedConversationId(id);
    setSelectedMessageIds(new Set());
  };

  const handleTogglePlatform = (platform: string) => {
    const newSet = new Set(selectedPlatforms);
    if (newSet.has(platform)) {
      newSet.delete(platform);
    } else {
      newSet.add(platform);
    }
    setSelectedPlatforms(newSet);
  };

  const handleTogglePriority = (priority: string) => {
    const newSet = new Set(selectedPriorities);
    if (newSet.has(priority)) {
      newSet.delete(priority);
    } else {
      newSet.add(priority);
    }
    setSelectedPriorities(newSet);
  };

  const handleBulkAction = (action: BulkActionPayload['action']) => {
    if (selectedMessageIds.size === 0) {
      toast.error('No messages selected');
      return;
    }

    bulkActionMutation.mutate({
      message_ids: Array.from(selectedMessageIds),
      action,
    });
  };

  const handleReply = async (text: string, isNote: boolean) => {
    await replyMutation.mutateAsync({ text, isNote });
  };

  const handleMarkAsRead = (id?: string) => {
    if (!id) {
      toast.error('No message selected');
      return;
    }
    updateMessageMutation.mutate({
      id,
      data: { status: 'read' },
    });
  };

  const handleStar = (id: string | undefined, currentValue: boolean) => {
    if (!id) {
      toast.error('No message selected');
      return;
    }
    updateMessageMutation.mutate({
      id,
      data: { is_starred: !currentValue },
    });
  };

  const handleArchive = (id?: string) => {
    if (!id) {
      toast.error('No message selected');
      return;
    }
    updateMessageMutation.mutate({
      id,
      data: { status: 'archived' },
    });
  };

  const handleSaveContactNotes = async (contactId: string, notes: string) => {
    await updateContactMutation.mutateAsync({
      id: contactId,
      data: { notes },
    });
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '/' && e.ctrlKey) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setShowPlatformFilter(false);
        setShowPriorityFilter(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  const messages: Message[] = selectedConversationData?.messages || [];

  return (
    <div className="h-full flex flex-col bg-n-card overflow-hidden">
      {/* === HEADER === */}
      <div className="px-6 pt-6 pb-4 border-b border-n-border">
        <h1 className="text-4xl font-bold text-n-text mb-1">📥 {t('inbox_page_title', 'Inbox')}</h1>
        <p className="text-sm text-n-text-md">{t('inbox_page_subtitle', 'Messages, comments, and mentions from all your channels.')}</p>
      </div>

      {/* === STATS BAR === */}
      <div className="bg-n-card border-b border-n-border px-4 py-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <StatBadge
              label="Unread"
              value={stats.unread_count}
              icon={Bell}
              color="blue"
              onClick={() => setActiveTab('unread')}
            />
            <StatBadge
              label="Avg Response"
              value={`${stats.avg_response_time_minutes}m`}
              icon={Clock3}
              color="green"
            />
            <StatBadge
              label="SLA Breaches"
              value={stats.sla_breaches_today}
              icon={AlertTriangle}
              color="red"
            />
            <StatBadge
              label="Total Conversations"
              value={stats.total_conversations}
              icon={MessageSquare}
              color="gray"
              onClick={() => setActiveTab('all')}
            />
          </div>

          <button className="p-2.5 hover:bg-slate-100 rounded-lg transition-all ml-auto">
            <Settings className="w-5 h-5 text-n-text-md" />
          </button>
        </div>
      </div>

      {/* === MAIN LAYOUT === */}
      <div className="flex-1 flex overflow-hidden gap-0">
        {/* === LEFT PANEL: CONVERSATIONS LIST === */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-80 border-r border-n-border/60 bg-n-card flex flex-col"
        >
          {/* Search & Filters - Premium Style */}
          <div className="p-4 border-b border-n-border/60 bg-n-card">
            {/* Search bar */}
            <div className="relative mb-4">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-n-text-lt" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder={t('inbox_search_placeholder', 'Search conversations... (Ctrl+/)')}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-11 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-500/20 text-sm transition-all bg-n-card"
              />
            </div>

            {/* Tab filters - Premium Style */}
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
              {[
                { key: 'all', label: t('inbox_all', 'All'), icon: MessageSquare },
                { key: 'unread', label: t('inbox_unread_tab', 'Unread'), icon: Bell },
                { key: 'starred', label: t('inbox_starred', 'Starred'), icon: Star },
                { key: 'assigned', label: t('inbox_assigned', 'Assigned'), icon: User },
              ].map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key as 'all' | 'unread' | 'starred' | 'assigned')}
                    className={`px-4 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-2 border ${
                      isActive
                        ? 'bg-n-card border-blue-200 text-blue-700'
                        : 'bg-n-card border-n-border/60 text-n-text-md hover:border-slate-300 hover:bg-n-page'
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${isActive ? 'text-blue-600' : 'text-n-text-lt'}`} />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Platform filters - Premium */}
            <div className="relative mb-2">
              <button
                onClick={() => setShowPlatformFilter(!showPlatformFilter)}
                className="w-full px-4 py-2 rounded-lg border border-n-border/60 text-xs font-semibold text-n-text-md hover:bg-n-page hover:border-slate-300 transition-all flex items-center justify-between bg-n-card"
              >
                <span className="flex items-center gap-2">
                  <Filter className="w-3.5 h-3.5 text-n-text-lt" />
                  {t('inbox_platforms', 'Platforms')} {selectedPlatforms.size > 0 && <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-[10px]">{selectedPlatforms.size}</span>}
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showPlatformFilter ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {showPlatformFilter && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-12 left-0 right-0 bg-n-card border border-n-border/60 rounded-lg shadow-sm z-40 p-3"
                  >
                    <div className="grid grid-cols-2 gap-2">
                      {PLATFORMS.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => handleTogglePlatform(p.id)}
                          className={`px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center gap-2 ${
                            selectedPlatforms.has(p.id)
                              ? `${p.bg} ${p.color} ring-2 ring-offset-1 ring-current`
                              : 'bg-slate-100 text-n-text hover:bg-slate-200'
                          }`}
                        >
                          <p.icon className="w-4 h-4" />
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Priority filters - Premium */}
            <div className="relative mt-2">
              <button
                onClick={() => setShowPriorityFilter(!showPriorityFilter)}
                className="w-full px-4 py-2 rounded-lg border border-n-border/60 text-xs font-semibold text-n-text-md hover:bg-n-page hover:border-slate-300 transition-all flex items-center justify-between bg-n-card"
              >
                <span className="flex items-center gap-2">
                  <AlertCircle className={`w-3.5 h-3.5 ${selectedPriorities.size > 0 ? 'text-blue-500' : 'text-n-text-lt'}`} />
                  {t('inbox_priority', 'Priority')} {selectedPriorities.size > 0 && <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-[10px]">{selectedPriorities.size}</span>}
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showPriorityFilter ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {showPriorityFilter && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-12 left-0 right-0 bg-n-card border border-n-border/60 rounded-lg shadow-sm z-40 p-3"
                  >
                    <div className="grid grid-cols-2 gap-2">
                      {PRIORITIES.map((p) => {
                        const config = PRIORITY_CONFIG[p];
                        return (
                          <button
                            key={p}
                            onClick={() => handleTogglePriority(p)}
                            className={`px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center gap-2 ${
                              selectedPriorities.has(p)
                                ? `${config.bg} ${config.color} ring-2 ring-offset-1 ring-current`
                                : 'bg-slate-100 text-n-text hover:bg-slate-200'
                            }`}
                          >
                            <div className={`w-2.5 h-2.5 rounded-full ${config.dot}`} />
                            {config.label}
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Bulk action bar - Premium */}
          <AnimatePresence>
            {selectedMessageIds.size > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="px-4 py-3 bg-n-card border-b border-n-border/60 flex items-center justify-between"
              >
                <span className="text-sm font-medium text-blue-900">
                  {selectedMessageIds.size} selected
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleBulkAction('archive')}
                    className="p-2 hover:bg-blue-100 rounded transition-colors"
                    title="Archive"
                  >
                    <Archive className="w-4 h-4 text-blue-600" />
                  </button>
                  <button
                    onClick={() => handleBulkAction('mark_read')}
                    className="p-2 hover:bg-blue-100 rounded transition-colors"
                    title="Mark as read"
                  >
                    <Check className="w-4 h-4 text-blue-600" />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex-1 overflow-y-auto pt-2 scrollbar-hide">
            {isLoadingMessages ? (
              <div className="flex items-center justify-center py-12">
                <Loader className="w-6 h-6 animate-spin text-slate-300" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <div className="w-16 h-16 rounded-3xl bg-n-page border border-n-border flex items-center justify-center mb-4">
                  <MessageSquare className="w-8 h-8 text-slate-300" />
                </div>
                <h3 className="text-sm font-bold text-slate-900 mb-1">{t('inbox_no_conversations', 'No conversations found')}</h3>
                <p className="text-xs text-slate-500 font-medium leading-relaxed">
                  Try adjusting your filters or search query to find what you&apos;re looking for.
                </p>
              </div>
            ) : (
              <div className="space-y-1 pb-4">
                {conversations.map((conv) => (
                  <ConversationCard
                    key={conv.id}
                    conversation={conv}
                    isSelected={selectedMessageIds.has(conv.latest_message_id)}
                    onSelect={(e) => {
                      e.stopPropagation();
                      const newSet = new Set(selectedMessageIds);
                      if (newSet.has(conv.latest_message_id)) {
                        newSet.delete(conv.latest_message_id);
                      } else {
                        newSet.add(conv.latest_message_id);
                      }
                      setSelectedMessageIds(newSet);
                    }}
                    onClickCard={() => handleSelectConversation(conv.id)}
                    isActive={selectedConversationId === conv.id}
                  />
                ))}
              </div>
            )}
          </div>
        </motion.div>

        {/* ---- CENTER: Thread ---- */}
        <div className="flex-1 flex flex-col min-w-0 bg-n-card/40">
          {selectedConversationId && selectedConversationData ? (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Thread Header */}
              <div className="px-6 py-4 border-b border-n-border/60 flex items-center justify-between bg-n-card/80 backdrop-blur-md">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    {selectedConversationData.sender_avatar ? (
                      <img
                        src={selectedConversationData.sender_avatar}
                        alt={selectedConversationData.sender_name}
                        className="w-10 h-10 rounded-xl object-cover border border-n-border shadow-sm"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-xl bg-n-green-bg border border-n-border flex items-center justify-center text-n-green font-bold">
                        {selectedConversationData.sender_name.charAt(0)}
                      </div>
                    )}
                    <div className="absolute -bottom-1 -right-1 bg-n-card p-1 rounded-lg shadow-sm border border-slate-50">
                      <PlatformIcon platform={selectedConversationData.platform} className="w-3 h-3" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-[15px] font-black text-slate-900 tracking-tight leading-none mb-1">
                      {selectedConversationData.sender_name}
                    </h3>
                    <div className="flex items-center gap-2">
                       <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                        {PLATFORMS.find((p) => p.id === selectedConversationData.platform)?.label}
                      </span>
                      <div className="w-1 h-1 rounded-full bg-slate-300" />
                      <span className="text-[10px] font-bold text-slate-400">
                        Thread ID: {selectedConversationData.id.slice(0, 8)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleStar(selectedConversationData.latest_message_id, selectedConversationData.is_starred)}
                    className={`p-2 rounded-xl transition-all duration-200 shadow-sm border ${
                      selectedConversationData.is_starred
                        ? 'bg-amber-50 border-amber-200 text-amber-500'
                        : 'bg-n-card border-n-border text-slate-400 hover:text-n-text-md hover:border-slate-300'
                    }`}
                  >
                    <Star className={`w-4 h-4 ${selectedConversationData.is_starred ? 'fill-current' : ''}`} />
                  </button>
                  <button
                    onClick={() => handleArchive(selectedConversationData.latest_message_id)}
                    className="p-2 bg-n-card border border-n-border text-slate-400 hover:text-n-text-md hover:border-slate-300 rounded-xl shadow-sm transition-all duration-200"
                  >
                    <Archive className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleMarkAsRead(selectedConversationData.latest_message_id)}
                    className="p-2 bg-n-card border border-n-border text-slate-400 hover:text-n-text-md hover:border-slate-300 rounded-xl shadow-sm transition-all duration-200"
                  >
                    {selectedConversationData.is_unread ? <Bell className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Messages Container */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6 scrollbar-hide bg-n-page/50">
                {isLoadingThread ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader className="w-8 h-8 animate-spin text-n-green/20" />
                  </div>
                ) : (
                  <>
                    {messages.map((msg) => (
                      <MessageBubble key={msg.id} message={msg} isOwn={msg.is_reply} />
                    ))}
                  </>
                )}
              </div>

              {/* Reply Section */}
              <div className="p-4 bg-n-card border-t border-n-border/60 shadow-lg shadow-slate-200/20">
                <ReplyComposer
                  conversationId={selectedConversationId}
                  onSend={handleReply}
                  isSending={replyMutation.isPending}
                  cannedResponses={cannedResponsesData || []}
                />
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center p-12 text-center">
              <div className="w-20 h-20 rounded-[2.5rem] bg-n-card border border-n-border/60 flex items-center justify-center mb-6 shadow-xl shadow-slate-200/40 relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-teal-500/5 rounded-[2.5rem] animate-pulse group-hover:scale-110 transition-transform duration-500" />
                <MessageSquare className="w-10 h-10 text-n-green relative z-10" />
              </div>
              <h2 className="text-xl font-black text-slate-900 tracking-tight mb-2">
                {t('inbox_select_to_start', 'Select a conversation to start')}
              </h2>
              <p className="max-w-xs text-sm text-slate-500 font-medium leading-relaxed">
                Choose a thread from the sidebar to view messages, reply, or manage interactions from all your platforms in one place.
              </p>
            </div>
          )}
        </div>

        {/* ---- RIGHT: Sidebar ---- */}
        <div className="hidden lg:block w-[320px] bg-n-card border-l border-n-border/60 overflow-y-auto shadow-sm">
          <ContactSidebar
            contact={contactData}
            isLoading={isLoadingContact}
            onSaveNotes={handleSaveContactNotes}
            isSavingNotes={updateContactMutation.isPending}
          />
        </div>
      </div>
    </div>
  );
}
